import axios from "axios";
import { shouldReportError } from "@/lib/errorThrottle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
  // Red de seguridad: una consulta fría a Boostr puede tardar ~20s; damos margen
  // amplio pero acotado para que una petición colgada falle como timeout manejable
  // (mensaje amable de reintento) en vez de quedar girando para siempre.
  timeout: 45000,
});

// Identificador de dispositivo persistente (para el cupo de consultas gratis de
// usuarios anónimos). No es PII; solo distingue navegadores.
const getDeviceId = () => {
  try {
    let id = localStorage.getItem("tf_device_id");
    if (!id) {
      id = (crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("tf_device_id", id);
    }
    return id;
  } catch {
    return "no-storage";
  }
};

api.interceptors.request.use((config) => {
  try {
    config.headers["X-Device-Id"] = getDeviceId();
  } catch { /* noop */ }
  return config;
});

export const clp = (n) =>
  "$" + Math.round(n || 0).toLocaleString("es-CL") + "";

export const riskColor = (level) =>
  level === "alto" ? "rojo" : level === "medio" ? "amarillo" : "verde";

// ---- Auto-salud: reintentos automáticos + reporte de errores ----
// 502/503/504 = errores de gateway estándar; 520-524 = errores transitorios de
// Cloudflare (p.ej. arranque en frío del backend tras un redeploy). Todos se
// reintentan automáticamente para que un hipo momentáneo no rompa la acción.
const RETRIABLE_STATUS = [502, 503, 504, 520, 521, 522, 523, 524];
const MAX_RETRIES = 2;
const SILENT = ["/api/errors/log", "/api/health"]; // no reportar ni reintentar-spamear

const isSilent = (url) => SILENT.some((s) => (url || "").includes(s));

const reportError = (payload) => {
  if (isSilent(payload.route) || isSilent(payload.meta?.url)) return;
  if (!shouldReportError(payload)) return; // throttle + dedupe
  try {
    api.post("/api/errors/log", payload).catch(() => {});
  } catch (err) {
    // El reporte de errores nunca debe romper la app.
    if (process.env.NODE_ENV !== "production") console.debug("reportError falló", err);
  }
};

// ¿La petición puede reintentarse de forma segura?
const canRetry = (cfg, status, isNetwork, isTimeout) => {
  // No reintentamos timeouts: una consulta fría legítima puede tardar ~20s; reintentar
  // encadenaría esperas de 45s. Mejor mostrar el mensaje de reintento manual.
  const retriable = (isNetwork && !isTimeout) || RETRIABLE_STATUS.includes(status);
  return (
    retriable &&
    (cfg.__retryCount || 0) < MAX_RETRIES &&
    !!cfg.method &&
    !cfg.__noRetry &&
    !isSilent(cfg.url)
  );
};

// ¿Debe reportarse este fallo final? (omitimos 401/403/404 esperados y endpoints silenciosos)
const shouldReport = (cfg, status) =>
  !isSilent(cfg.url) && ![401, 403, 404].includes(status);

api.interceptors.response.use(
  (res) => {
    const cfg = res.config || {};
    if (cfg.__retryCount > 0 && !isSilent(cfg.url)) {
      reportError({
        message: `Auto-recuperado tras ${cfg.__retryCount} reintento(s): ${cfg.url}`,
        route: window.location.pathname,
        type: "auto_heal",
        meta: { url: cfg.url },
      });
    }
    return res;
  },
  async (error) => {
    const cfg = error.config || {};
    const status = error.response?.status;
    const isNetwork = !error.response;
    const isTimeout = error.code === "ECONNABORTED";
    cfg.__retryCount = cfg.__retryCount || 0;

    if (canRetry(cfg, status, isNetwork, isTimeout)) {
      cfg.__retryCount += 1;
      const delay = 400 * Math.pow(2, cfg.__retryCount - 1);
      await new Promise((r) => setTimeout(r, delay));
      return api(cfg);
    }

    if (shouldReport(cfg, status)) {
      reportError({
        message: `${isNetwork ? "Network Error" : "HTTP " + status}: ${(cfg.method || "").toUpperCase()} ${cfg.url || ""}`,
        route: window.location.pathname,
        status: status || null,
        type: "error",
        meta: { url: cfg.url, network: isNetwork },
      });
    }
    return Promise.reject(error);
  }
);

export default api;
